import { Button } from '@components/utility/buttons';
import { Checkbox } from '@components/utility/checkboxes';
import { Box, Divider, Stack, Typography, useMediaQuery } from '@mui/material';
import { useGetNotificationSettingsQuery, usePatchNotificationSettingsMutation } from '@notifications/services';
import { NotificationSettings } from '@notifications/types';
import { handleError, toaster } from '@utils/common';
import { FormikProvider, useFormik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

const NotificationCheckboxes: { label: JSX.Element; name: keyof NotificationSettings }[] = [
    {
        label: <FormattedMessage id="notifications.event_invitation" defaultMessage="Event Invitations" />,
        name: 'event_invitation',
    },
    {
        label: <FormattedMessage id="notifications.event_reminder" defaultMessage="Event Reminders" />,
        name: 'event_reminder',
    },
    {
        label: <FormattedMessage id="notifications.new_badge_earned" defaultMessage="Earned Badges" />,
        name: 'new_badge_earned',
    },
];

export const AccountSettings = () => {
    const [patchSettings, { isLoading: isUpdatingSettings }] = usePatchNotificationSettingsMutation();
    const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const intl = useIntl();

    const { data, isFetching: isFetchingSettings } = useGetNotificationSettingsQuery();

    const formik = useFormik<NotificationSettings>({
        initialValues: data || {
            event_invitation: false,
            event_reminder: false,
            new_badge_earned: false,
        },
        onSubmit: async (values, helpers) => {
            try {
                await patchSettings(values).unwrap();
                helpers.resetForm({ values });
                toaster.success({
                    title: intl.formatMessage({
                        id: 'account.settings.updateNotificationSettingsSuccess',
                        defaultMessage: 'Notification settings updated successfully',
                    }),
                    msg: intl.formatMessage({
                        id: 'account.settings.updateNotificationSettingsSuccessMsg',
                        defaultMessage: 'Your notification settings have been updated.',
                    }),
                });
            } catch (error) {
                console.error('Error updating notification settings:', error);
                handleError(
                    intl.formatMessage({
                        id: 'account.settings.updateNotificationSettingsError',
                        defaultMessage: 'Error updating notification settings',
                    }),
                    error
                );
            }
        },
        enableReinitialize: true,
    });

    return (
        <>
            <title>Account Settings - Victron Professional</title>
            <Box bgcolor="#fff" className="black-border" borderRadius="5px" p="24px">
                <FormikProvider value={formik}>
                    <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                        <Typography py="26px" fontFamily="museo-sans" letterSpacing="-1%" fontSize="26px">
                            <FormattedMessage
                                id="account.settings.notificationManagement"
                                defaultMessage="Notification Management"
                            />
                        </Typography>
                        <Divider />
                        <Box my="24px">
                            <Stack direction="column" spacing="10px">
                                <Typography
                                    fontFamily="museo-sans"
                                    fontSize="16px"
                                    fontWeight={600}
                                    color="neutral-900"
                                >
                                    <FormattedMessage
                                        id="account.settings.notificationCategories"
                                        defaultMessage="To which categories you wish to receive notifications?"
                                    />
                                </Typography>
                                {NotificationCheckboxes.map((checkbox) => (
                                    <Checkbox
                                        key={checkbox.name}
                                        labelProps={{ label: checkbox.label }}
                                        name={checkbox.name}
                                        checked={formik.values[checkbox.name]}
                                        onChange={formik.handleChange}
                                        loading={isFetchingSettings}
                                    />
                                ))}
                            </Stack>
                        </Box>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems="center"
                            justifySelf={{ md: 'end' }}
                            mt="24px"
                            gap="15px"
                        >
                            <Button.Neutral
                                fullWidth={isBelowMd}
                                size="large"
                                sx={{ bgcolor: 'neutral-650.main' }}
                                onClick={formik.handleReset}
                                disabled={!formik.dirty}
                                className="font-semibold"
                            >
                                <FormattedMessage id="common.revertChanges" defaultMessage="Revert Changes" />
                            </Button.Neutral>
                            <Button
                                size="large"
                                type="submit"
                                fullRounded
                                fullWidth={isBelowMd}
                                loading={isUpdatingSettings}
                                disabled={isUpdatingSettings || !formik.dirty || !formik.isValid}
                                loadingPosition="start"
                                className="font-semibold"
                            >
                                <FormattedMessage id="common.saveChanges" defaultMessage="Save Changes" />
                            </Button>
                        </Stack>
                    </form>
                </FormikProvider>
            </Box>
        </>
    );
};
